import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useVerifiedCommandPermissions } from "./CommandPermissionsContext";
import { client } from "Service/axios";
import * as API from "Shared/Constants/apiRequest";
import { INSPECTIONS_FORMS, USERS } from "Shared/Constants/routes";
import { CommandKind, CommandRecord, CommandScope } from "./types";
import { isCommandScopeCurrent } from "./commandSession";
import { formatPersonName } from "Shared/Utils/personName";

export const kindLabels: Record<CommandKind, string> = {
  vehicle: "Vehicle",
  workOrder: "Work order",
  issue: "Issue",
  serviceReminder: "Service reminder",
  vehicleReminder: "Vehicle renewal",
  contactReminder: "Contact renewal",
  vendor: "Vendor",
  other: "Record",
};
export const recordEndpoints: Partial<Record<CommandKind, string>> = {
  vehicle: API.VEHICLES,
  workOrder: API.WORK_ORDER,
  issue: API.ISSUES,
  serviceReminder: API.REMINDERS_SERVICES,
  vehicleReminder: API.REMINDERS_VEHICLES,
  contactReminder: API.REMINDERS_CONTACTS,
  vendor: API.VENDORS,
};
const routes: Record<CommandKind, string> = {
  vehicle: "/units",
  workOrder: "/work-orders",
  issue: "/issues",
  serviceReminder: "/reminders/services",
  vehicleReminder: "/reminders/vehicles",
  contactReminder: "/reminders/contacts",
  vendor: "/vendor",
  other: "",
};
export function label(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "object") {
    const v = value as Record<string, any>;
    return (
      v.name ||
      v.title ||
      formatPersonName(v) ||
      ""
    );
  }
  return String(value);
}
export function humanize(value: unknown): string {
  return label(value)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ");
}
export function rowsOf(payload: any): Record<string, any>[] {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}
export function toCommandRecord(
  kind: CommandKind,
  data: Record<string, any>,
): CommandRecord | null {
  if (!data || data.id == null || kind === "other") return null;
  const vehicle =
    typeof data.vehicle === "object" && data.vehicle?.id != null
      ? { id: data.vehicle.id, name: label(data.vehicle) || "Vehicle" }
      : typeof data.vehicle === "string"
        ? { id: data.vehicle, name: "Vehicle" }
        : undefined;
  const ref =
    data.reference_code != null
      ? `#${data.reference_code}`
      : String(data.id).slice(0, 8);
  const title =
    kind === "vehicle" || kind === "vendor"
      ? label(data.name) || `${kindLabels[kind]} ${ref}`
      : kind === "workOrder"
        ? `Work order ${ref}`
        : kind === "issue"
          ? label(data.summary) || `Issue ${ref}`
          : (typeof data.service_task === "object"
              ? label(data.service_task)
              : "") ||
            (typeof data.renewal_type === "object"
              ? label(data.renewal_type)
              : "") ||
            `${kindLabels[kind]} ${ref}`;
  const subtitle =
    kind === "vehicle"
      ? [data.year, label(data.make), label(data.model), data.license_plate]
          .filter(Boolean)
          .join(" · ")
      : kind === "vendor"
        ? [data.address, data.city].filter(Boolean).join(" · ")
        : [
            vehicle?.name,
            kind === "contactReminder" && typeof data.contact === "object"
              ? label(data.contact)
              : "",
            kindLabels[kind],
          ]
            .filter(Boolean)
            .join(" · ");
  return {
    key: `${kind}:${data.id}`,
    id: data.id,
    kind,
    title,
    subtitle,
    status: humanize(data.status),
    href: `${routes[kind]}/${encodeURIComponent(data.id)}`,
    vehicle,
    data,
  };
}

// Unknown categories are excluded deliberately; a new endpoint cannot silently gain visibility.
const categories: Record<string, CommandKind> = {
  Vehicle: "vehicle",
  Work_Order: "workOrder",
  Issue: "issue",
  Vendor: "vendor",
  Service_Reminders: "serviceReminder",
  Vehicle_Renewals: "vehicleReminder",
  Contact_Renewals: "contactReminder",
};
const extras: Record<
  string,
  { route: string; module: string; permissions: string[]; title: string }
> = {
  Contact: {
    route: USERS,
    module: "contacts",
    permissions: ["view_contacts", "full_contacts_access"],
    title: "Contact",
  },
  Equipment: {
    route: "/equipments",
    module: "equipments",
    permissions: ["view_equipment", "full_equipment_access"],
    title: "Equipment",
  },
  Part: {
    route: "/parts",
    module: "inventory",
    permissions: ["view_parts", "full_parts_access"],
    title: "Part",
  },
  Service: {
    route: "/service-history",
    module: "service_entries",
    permissions: ["view_service_entries", "full_service_entries_access"],
    title: "Service",
  },
  Inspection_Form: {
    route: INSPECTIONS_FORMS,
    module: "inspections",
    permissions: ["view_inspections", "full_inspections_access"],
    title: "Inspection form",
  },
  Inspection_Submission: {
    route: "/inspections/history",
    module: "inspections",
    permissions: ["view_inspections", "full_inspections_access"],
    title: "Inspection",
  },
  Fuel: {
    route: "/fuel-history",
    module: "fuel_entries",
    permissions: ["view_fuel_entries", "full_fuel_entries_access"],
    title: "Fuel entry",
  },
  Purchase_Order: {
    route: "/purchase-orders",
    module: "purchase_orders",
    permissions: ["manage_purchase_orders", "full_purchase_orders_access"],
    title: "Purchase order",
  },
  Documents: {
    route: "/papers",
    module: "documents",
    permissions: ["view_fleet_documents", "full_documents_access"],
    title: "Document",
  },
  FaultCode: {
    route: "/faults",
    module: "faults",
    permissions: ["full_faults_access"],
    title: "Fault",
  },
};
export type SearchGrants = {
  full: boolean;
  modules: string[];
  permissions: string[];
};
export function mapGlobalResults(
  payload: any,
  allowed: CommandKind[],
  grants: SearchGrants,
): CommandRecord[] {
  const results: CommandRecord[] = [];
  Object.entries(payload || {}).forEach(([category, group]) => {
    const kind = categories[category];
    const extra = extras[category];
    if (kind && !allowed.includes(kind)) return;
    if (
      !kind &&
      (!extra ||
        !allowed.includes("other") ||
        (!grants.full &&
          !(
            grants.modules.includes(extra.module) &&
            extra.permissions.some((p) => grants.permissions.includes(p))
          )))
    )
      return;
    rowsOf((group as any)?.records).forEach((row) => {
      if (row?.id == null) return;
      const record = kind
        ? toCommandRecord(kind, row)
        : {
            key: `${category}:${row.id}`,
            id: row.id,
            kind: "other" as const,
            title:
              label(
                row.name ||
                  row.title ||
                  row.summary ||
                  row.part_number ||
                  row.po_number ||
                  row.original_file_name ||
                  row.file_name,
              ) ||
              label(row) ||
              `${extra.title} ${row.reference_code != null ? `#${row.reference_code}` : String(row.id).slice(0, 8)}`,
            subtitle: [extra.title, label(row.vehicle), row.email]
              .filter(Boolean)
              .join(" · "),
            href:
              category === "Documents" || category === "FaultCode"
                ? extra.route
                : `${extra.route}/${encodeURIComponent(row.id)}`,
            data: { ...row, commandCategory: extra.title },
          };
      if (record) results.push(record);
    });
  });
  return results;
}

interface CacheEntry {
  records: CommandRecord[];
  updatedAt: number;
}
interface ScopeCache {
  owner: string;
  queries: Map<string, CacheEntry>;
  records: Map<string, CommandRecord>;
}
const caches = new Map<string, ScopeCache>();
const listeners = new Set<() => void>();
let generation = 0;
export function invalidateCommandCache(scopeKey?: string) {
  generation += 1;
  if (!scopeKey) caches.clear();
  else
    caches.forEach((cache, key) => {
      if (cache.owner === scopeKey) caches.delete(key);
    });
  listeners.forEach((listener) => listener());
}
export const clearCommandCache = () => invalidateCommandCache();
export function commandCacheIdentity(
  scope: CommandScope,
  allowed: CommandKind[],
  grants: SearchGrants,
) {
  return JSON.stringify([
    scope.key,
    scope.userId,
    scope.companyId,
    [...allowed].sort(),
    grants.full,
    [...grants.modules].sort(),
    [...grants.permissions].sort(),
  ]);
}
function cacheFor(identity: string, owner: string): ScopeCache {
  let cache = caches.get(identity);
  if (!cache) {
    cache = { owner, queries: new Map(), records: new Map() };
    caches.set(identity, cache);
    if (caches.size > 8) caches.delete(caches.keys().next().value!);
  }
  return cache;
}
function dedupe(records: CommandRecord[]) {
  return [...new Map(records.map((record) => [record.key, record])).values()];
}
export function filterCommandRecords(
  records: CommandRecord[],
  query: string,
  kind?: CommandKind,
) {
  const terms = query.toLocaleLowerCase().trim().split(/\s+/).filter(Boolean);
  return records.filter(
    (record) =>
      (!kind || record.kind === kind) &&
      terms.every((term) =>
        [
          record.title,
          record.subtitle,
          record.status,
          record.id,
          record.data?.vin_sn,
          record.data?.reference_code,
        ]
          .join(" ")
          .toLocaleLowerCase()
          .includes(term),
      ),
  );
}

export function useCommandSearch({
  scope,
  query,
  kind,
  enabled,
  allowedKinds,
}: {
  scope: CommandScope;
  query: string;
  kind?: CommandKind;
  enabled: boolean;
  allowedKinds: CommandKind[];
}) {
  const permissions = useVerifiedCommandPermissions();
  const grants: SearchGrants = {
    full: Boolean(permissions?.has_full_access),
    modules: Array.from(permissions?.modules || []),
    permissions: Array.from(permissions?.available_permissions || []),
  };
  const inputs = useRef({ scope, query, kind, allowedKinds, grants });
  inputs.current = { scope, query, kind, allowedKinds, grants };
  const identity = commandCacheIdentity(scope, allowedKinds, grants);
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const requestKey = JSON.stringify([kind || "all", normalizedQuery]);
  const stateKey = identity + requestKey;
  const [revision, refresh] = useReducer((value) => value + 1, 0);
  const [state, setState] = useState<{
    key: string;
    pending: boolean;
    error: string | null;
  }>({ key: "", pending: false, error: null });
  useEffect(() => {
    listeners.add(refresh);
    return () => {
      listeners.delete(refresh);
    };
  }, []);
  const cache = caches.get(identity);
  const exact = cache?.queries.get(requestKey);
  const records =
    exact?.records ||
    filterCommandRecords(
      Array.from(cache?.records.values() || []),
      query,
      kind,
    ).slice(0, 40);
  useEffect(() => {
    const { scope, query, kind, allowedKinds, grants } = inputs.current;
    const normalizedQuery = query.trim().toLocaleLowerCase();
    const stateKey = identity + requestKey;
    if (
      !enabled ||
      !isCommandScopeCurrent(scope) ||
      !allowedKinds.length ||
      (kind && !allowedKinds.includes(kind))
    )
      return;
    let active = true;
    const controller = new AbortController();
    const startedGeneration = generation;
    setState({ key: stateKey, pending: true, error: null });
    const timeout = window.setTimeout(
      async () => {
        if (!isCommandScopeCurrent(scope)) return;
        const jobs: Promise<CommandRecord[]>[] = [];
        const list = (k: CommandKind, term: string) =>
          client
            .get(recordEndpoints[k]!, {
              params: {
                limit: 20,
                offset: 0,
                ...(term ? { search: term } : {}),
              },
              signal: controller.signal,
            })
            .then((response) =>
              rowsOf(response.data)
                .map((row) => toCommandRecord(k, row))
                .filter((record): record is CommandRecord => Boolean(record)),
            );
        if (normalizedQuery) {
          jobs.push(
            client
              .get(API.GLOBAL_SEARCH, {
                params: { query: query.trim(), refresh: true },
                signal: controller.signal,
              })
              .then((response) =>
                mapGlobalResults(response.data, allowedKinds, grants).filter(
                  (record) => !kind || record.kind === kind,
                ),
              ),
          );
          // Global search omits contact renewals and limits categories to three records.
          const supplemental = kind
            ? [kind]
            : ([
                "workOrder",
                "serviceReminder",
                "vehicleReminder",
                "contactReminder",
              ] as CommandKind[]);
          supplemental
            .filter((k) => allowedKinds.includes(k) && recordEndpoints[k])
            .forEach((k) => jobs.push(list(k, query.trim())));
        } else {
          const browse = kind
            ? [kind]
            : allowedKinds.filter((k) => recordEndpoints[k]);
          browse
            .filter((k) => recordEndpoints[k])
            .forEach((k) => jobs.push(list(k, "")));
        }
        const responses = await Promise.allSettled(jobs);
        if (
          !active ||
          controller.signal.aborted ||
          generation !== startedGeneration ||
          !isCommandScopeCurrent(scope)
        )
          return;
        const successful = responses.filter(
          (result): result is PromiseFulfilledResult<CommandRecord[]> =>
            result.status === "fulfilled",
        );
        const failed = responses.length - successful.length;
        if (successful.length) {
          const next = dedupe(
            successful.flatMap((result) => result.value),
          ).slice(0, 80);
          const target = cacheFor(identity, scope.key);
          target.queries.set(requestKey, {
            records: next,
            updatedAt: Date.now(),
          });
          next.forEach((record) => {
            target.records.delete(record.key);
            target.records.set(record.key, record);
          });
          if (target.queries.size > 30)
            target.queries.delete(target.queries.keys().next().value!);
          while (target.records.size > 300)
            target.records.delete(target.records.keys().next().value!);
        }
        setState({
          key: stateKey,
          pending: false,
          error: failed
            ? successful.length
              ? "Some results could not be refreshed. Retry to search all available records."
              : "Could not load records. Check your connection and try again."
            : null,
        });
      },
      normalizedQuery ? 180 : 0,
    );
    return () => {
      active = false;
      controller.abort();
      window.clearTimeout(timeout);
    };
    // Stable serialized identities cover the scope, permission sets and query inputs.
  }, [identity, requestKey, enabled, revision]);
  const pending =
    enabled &&
    allowedKinds.length > 0 &&
    (!kind || allowedKinds.includes(kind)) &&
    isCommandScopeCurrent(scope) &&
    (state.key !== stateKey || state.pending);
  return {
    records: enabled && isCommandScopeCurrent(scope) ? records : [],
    loading: pending && records.length === 0,
    refreshing: pending && records.length > 0,
    error: state.key === stateKey ? state.error : null,
    updatedAt: exact?.updatedAt || null,
    refresh: useCallback(() => refresh(), []),
  };
}
